(ns ycweb.app.blitz.shipping.swad
  (:require
   [rum.core :as rum]
   [ycweb.components.header :as header]
   [ycweb.util.core :as u]
   [ycweb.components.common :as cmn]
   [ycweb.app.pos.menu :as menu]
   [ycweb.components.transitions :as t]
   [ycweb.components.grid :as grid]
   [ycweb.components.wrapper :as w]
   [ycweb.components.flow :as flow]
   [ycweb.util.sound :as sound]
   [ajax.core :as ajax]
   [ycweb.components.forms :as forms]
   [ycweb.components.toaster :as toaster]
   [ycweb.util.comms :as comms]
   [clojure.string :as str]))

(def steps
  (forms/setup-steps
    [{:key :start :title "Scan Swad Machine"}
     {:key :scan-pb :title "Scan Packbox"}
     {:key :scan-result :title "Scan Result"}]))

(defmethod flow/barcode-dispatch-screen :swad/result [_ app-atom scan]
  (grid/dispatch-action app-atom :swad/packbox-scan {:scan scan}))

(defn- pending-count [pendingboxes]
  (if (u/presence pendingboxes)
    (count (str/split pendingboxes #", "))
    0))

(defn- pending-text [pending-count]
  (str pending-count (if (= 1 pending-count) " more box" " more boxes") " to scan"))

(rum/defc result-screen < rum/reactive
  [app-atom]
  (let [swadscreen-atom (rum/cursor-in app-atom [:swad :screen])
        {:keys [fail-reason courier servicetype pb-last4 order-number pb-number success description complete remaining pendingboxes]}
        (rum/react swadscreen-atom)
        menu-atom (rum/cursor app-atom :menu)
        pending-count (pending-count pendingboxes)]
    [:div.page-body
     {:class
      (u/bem :page-body
        [:swad
         (cond
           (and success (pos? pending-count)) :swad-pending
           success :swad-success
           :else :swad-error)])}
     (header/header app-atom "SWAD screen" {})
     (header/menu-overlay menu-atom)
     (header/menu app-atom (menu/select-menus app-atom))
     [:div.page-body__main-content
      [:div.page-body__main-content
       [:div.section
        [:h1.u-text-center
         {:class (u/bem :swad-heading [:standard])}
         (str "Parcel " pb-number " | Order: " order-number)
         [:span.u-hidden-from-view " | "]
         [:span.swad-heading__subtext (str remaining " parcels remaining")]
         [:span.swad-heading__subtext description]]
        [:h2.u-text-center
         {:class (u/bem :swad-heading [:enormous])}
         (cond
           (and success (pos? pending-count))
           (pending-text pending-count)

           success
           (str courier ": " servicetype " (" pb-last4 ")")

           :else
           (str fail-reason " (" pb-last4 ")"))]
        [:i.u-block-center
         {:class (u/bem :icon [:block :is-light :enormous (if success :emoticon-happy :emoticon-sad)])}]
        [:p.u-text-center
         {:class (u/bem :swad-heading [:flush :large])}
         (cond
           (and success (pos? pending-count))
           (str "Box " pb-last4 " okay. Now scan: " pendingboxes)

           success
           "Box okay!"

           :else
           "Check this parcel")]
        [:p.u-text-center
         {:class (u/bem :swad-heading [:flush :standard])}
         (when complete (str order-number " completed!"))]]]]]))

(defmethod flow/render-screen :swad/result [app-atom _]
  (result-screen app-atom))

(defmethod comms/receive :swad/packbox-scan [app-atom {{:keys [success pendingboxes]} :screen :keys [screen tts]}]
  (toaster/close-subtle-progress app-atom)
  (swap! app-atom assoc-in [:swad :screen] screen)
  (t/transition-next app-atom :swad/result)
  (let [pending-count (pending-count pendingboxes)]
    (sound/tts
      (if (and success (pos? pending-count))
        (pending-text pending-count)
        tts))))

;; The SWAD machine is a Cubiscan: GET /csMeasure starts a reading and GET /csMeasureData
;; returns the latest one as {"measurement" {...}} in cm/kg. /csMeasureData keeps returning
;; the previous reading, so a new one is recognised by package_count moving on (or, for a
;; failed reading which isn't counted, by seeing status go 1 -> 0).
(def poll-ms 300) ; the Cubiscan web server falls over when polled faster than 250ms
(def measure-timeout-ms 10000)

(defn- cubiscan-get [uri path handler error-handler]
  (ajax/GET (str uri path)
    {:response-format :json
     :keywords? true
     :handler handler
     :error-handler (fn [_] (error-handler "Cannot find SWAD machine"))}))

(defn- measure
  "Triggers a reading and calls on-dims with {:x :y :z} in mm and :w in g, or on-error with a message."
  [uri on-dims on-error]
  #?(:cljs
     (cubiscan-get uri "/csMeasureData"
       (fn [{{baseline :package_count} :measurement}]
         (cubiscan-get uri "/csMeasure"
           (fn [{{:keys [status]} :measure}]
             (if (not= 0 status)
               (on-error "SWAD machine did not start measuring")
               (let [deadline (+ (js/Date.now) measure-timeout-ms)
                     poll
                     (fn poll [seen-measuring?]
                       (cubiscan-get uri "/csMeasureData"
                         (fn [{{:keys [status package_count length width height weight] :as m} :measurement}]
                           ;; Failed readings don't move package_count, so a reading is also done once
                           ;; we've seen it measuring (status 1) and come back to status 0.
                           (let [done?
                                 (and
                                   (= 0 status)
                                   (or seen-measuring? (not= package_count baseline)))]
                             (cond
                               ;; Flat items can legitimately read a 0 dimension (the backend skips the
                               ;; dims check for flat packaging), but a missing value, a per-field error
                               ;; or a weight <= 0 (unzeroed scale) is never a real parcel.
                               (and
                                 done?
                                 (or
                                   (some (comp #(not= 0 %) m) [:length_status :width_status :height_status :weight_status])
                                   (some (comp #(not (and (number? %) (>= % 0))) m) [:length :width :height])
                                   (not (and (number? weight) (pos? weight)))))
                               (on-error "Invalid reading, zero the SWAD machine and re-measure")

                               done?
                               (on-dims
                                 {:x (js/Math.round (* 10 length))
                                  :y (js/Math.round (* 10 width))
                                  :z (js/Math.round (* 10 height))
                                  :w (js/Math.round (* 1000 weight))})

                               (< (js/Date.now) deadline)
                               (js/setTimeout #(poll (or seen-measuring? (= 1 status))) poll-ms)

                               :else
                               (on-error "SWAD machine timed out"))))
                         on-error))]
                 (js/setTimeout #(poll false) 1000))))
           on-error))
       on-error)))

(defmethod grid/action :swad/packbox-scan [app-atom {:keys [scan]} _]
  (toaster/open-subtle-progress app-atom)
  (let [{:keys [uri swadmachine-id]} (get @app-atom :swad)]
    (measure uri
      (fn [dims]
        (comms/send :swad/packbox-scan
          {:scan scan :dims dims :swadmachine-id swadmachine-id}))
      (fn [msg]
        (toaster/close-subtle-progress app-atom)
        (toaster/open-toast app-atom :error msg {})))))

(defmethod flow/barcode-dispatch-screen :swad/packbox-scan [_ app-atom scan]
  (grid/dispatch-action app-atom :swad/packbox-scan {:scan scan}))

(rum/defc packbox-scan < rum/reactive [app-atom]
  (let [remaining (cmn/curact app-atom [:swad :remaining])]
    (w/wrapper app-atom "SWAD"
      {:step :scan-pb
       :steps steps}
      [[:div.section
        [:h1.u-text-center
         {:class (u/bem :swad-heading [:standard])}
         (str remaining " parcels remaining")]
        [:h2.u-text-center
         {:class (u/bem :heading [:standard])}
         "Scan Packbox"]
        grid/barcode-icon]]
      [[:p.paragraph.paragraph--flush.u-text-center.u-margin-top-24
        "Scan the packbox you want to ship"]]

      (grid/pos-call-to-action
        {:left-fn #(t/transition-prev app-atom)
         :right-text "Scan SWAD Machine"
         :right-plain true}))))

(defmethod flow/render-screen :swad/packbox-scan [app-atom _]
  (packbox-scan app-atom))

(defmethod comms/receive :swad/machine-scan [app-atom {:keys [swadmachine-id remaining]}]
  (toaster/close-subtle-progress app-atom)
  (swap! app-atom update :swad assoc
    :swadmachine-id swadmachine-id
    :remaining remaining)
  (t/transition-next app-atom :swad/packbox-scan))

(defmethod grid/action :swad/machine-scan [app-atom {:keys [scan]} _]
  (toaster/open-subtle-progress app-atom)
  (let [uri (str "http://" scan)]
    (cubiscan-get uri "/csMeasureData"
      (fn [{{:keys [serial_number]} :measurement}]
        (cond
          serial_number
          (do
            (swap! app-atom assoc-in [:swad :uri] uri)
            ;; swadmachine rows are keyed by :mac; the Cubiscan serial stands in for it
            (comms/send :swad/machine-scan {:mac serial_number}))

          :else
          (do
            (toaster/close-subtle-progress app-atom)
            (toaster/open-toast app-atom :error "SWAD machine not ready" {}))))
      (fn [msg]
        (toaster/close-subtle-progress app-atom)
        (toaster/open-toast app-atom :error msg {})))))

(defmethod flow/barcode-dispatch-screen :swad/machine-scan [_ app-atom scan]
  (grid/dispatch-action app-atom :swad/machine-scan {:scan scan}))

(rum/defc swad-scan < rum/reactive [app-atom]
  (w/wrapper app-atom "SWAD" {:steps steps :step :start}
    [[:div.section
      [:h2.u-text-center
       {:class (u/bem :heading [:standard])}
       "Scan SWAD Machine"]
      grid/barcode-icon]]
    [[:p.paragraph.paragraph--flush.u-text-center.u-margin-top-24
      "Scan the SWAD machine you'll be using"]]

    (grid/pos-call-to-action
      {:left-fn #(t/transition-prev app-atom)
       :right-text "Scan SWAD Machine"
       :right-plain true})))

(defmethod flow/render-screen :swad/machine-scan [app-atom _]
  (swad-scan app-atom))
